import { AbstractMesh, CascadedShadowGenerator, Color3, DirectionalLight, Light, Mesh, ShadowGenerator, Vector3 } from '@babylonjs/core';
import { BabylonjsModule } from '@modules/babylonjs/src/BabylonjsModule';
import { Injectable } from '@modules/core';
import { EnvironmentLoader } from '@modules/standard/src/loader/EnvironmentLoader';

import environmentUrl from './assets/environment/environment.env';

export class LightManager implements Injectable {
  static readonly INJECTABLE_TOKEN = Symbol.for('LightManager');

  //------------------------------------------------------------------------------------
  // #### System Base ####
  //------------------------------------------------------------------------------------
  private readonly scene;
  private readonly environmentLoader;
  constructor({
    [BabylonjsModule.INJECTABLE_TOKEN]: babylonjs,
    [EnvironmentLoader.INJECTABLE_TOKEN]: environmentLoader,
  }: {
    [BabylonjsModule.INJECTABLE_TOKEN]: Pick<BabylonjsModule, 'scene'>;
    [EnvironmentLoader.INJECTABLE_TOKEN]: EnvironmentLoader;
  }) {
    this.scene = babylonjs.scene;
    this.environmentLoader = environmentLoader;
  }

  dispose() {
    this._mainLight.dispose();
    this._shadowGenerator.dispose();
    this._subLightShadowGenerator.dispose();
  }

  private _mainLight!: DirectionalLight;
  private _subLight!: DirectionalLight;
  private _shadowGenerator!: ShadowGenerator;
  private _subLightShadowGenerator!: CascadedShadowGenerator;

  public get mainLight() {
    return this._mainLight;
  }

  public get shadowGenerator() {
    return this._shadowGenerator;
  }

  public get subLight() {
    return this._subLight;
  }

  public get subLightShadowGenerator() {
    return this._subLightShadowGenerator;
  }

  public initializeLight() {
    this.environmentLoader.applyEnvironment(this.scene, environmentUrl, {
      intensity: 1.5,
    });

    this._mainLight = new DirectionalLight('Directional Light Main', new Vector3(1, -1, 0), this.scene);
    this._mainLight.position = new Vector3(0, 0, 0);
    this._mainLight.diffuse = Color3.White();
    this._mainLight.intensity = 2.8;
    this._mainLight.intensityMode = Light.INTENSITYMODE_AUTOMATIC;
    this._mainLight.autoCalcShadowZBounds = true;

    this._shadowGenerator = new ShadowGenerator(1024, this._mainLight);
    this._shadowGenerator.useCloseExponentialShadowMap = true;
    this._shadowGenerator.bias = 0.01;
    this._shadowGenerator.blurScale = 1;
    this._shadowGenerator.darkness = 0;
    this._shadowGenerator.filter = ShadowGenerator.FILTER_PCF;

    this._subLight = new DirectionalLight('Directional Light Sub', new Vector3(1, -1, 0), this.scene);
    this._subLight.position = new Vector3(0, 0, 0);
    this._subLight.diffuse = Color3.White();
    this._subLight.intensity = 1.25;
    this._subLight.intensityMode = Light.INTENSITYMODE_AUTOMATIC;
    this._subLight.autoCalcShadowZBounds = true;
    this._subLight.setEnabled(false);

    this._subLightShadowGenerator = new CascadedShadowGenerator(1024, this._subLight);
    this._subLightShadowGenerator.numCascades = 2;
    this._subLightShadowGenerator.depthClamp = true;
    this._subLightShadowGenerator.lambda = 1;
    this._subLightShadowGenerator.setMinMaxDistance(0, 0.1);
    this._subLightShadowGenerator.shadowMaxZ = 1000;
    this._subLightShadowGenerator.stabilizeCascades = true;
    this._subLightShadowGenerator.useCloseExponentialShadowMap = true;
    this._subLightShadowGenerator.filter = ShadowGenerator.FILTER_PCF;
    this._subLightShadowGenerator.darkness = 0;
  }

  public addShadowCaster(m: Mesh | AbstractMesh, isSubLight: boolean = false) {
    if (isSubLight) {
      this._subLightShadowGenerator.addShadowCaster(m);
    } else {
      this._shadowGenerator.addShadowCaster(m);
    }
  }

  public addMainLightRenderList(m: Mesh | AbstractMesh, isSubLight: boolean = false) {
    if (isSubLight) {
      this._subLight.includedOnlyMeshes.push(m);
    } else {
      this._mainLight.includedOnlyMeshes.push(m);
    }
  }

  public removeShadowCaster(m: Mesh | AbstractMesh, isSubLight: boolean = false) {
    if (isSubLight) {
      this._subLightShadowGenerator.removeShadowCaster(m);
    } else {
      this._shadowGenerator.removeShadowCaster(m);
    }
  }

  public removeMainLightRenderList(m: Mesh | AbstractMesh, isSubLight: boolean = false ) {
    if (isSubLight) {
      this._subLight.includedOnlyMeshes = this._subLight.includedOnlyMeshes.filter((mesh) => mesh !== m);
    } else {
      this._mainLight.includedOnlyMeshes = this._mainLight.includedOnlyMeshes.filter((mesh) => mesh !== m);
    }
  }
}
